import AdSenseBanner from "@/components/AdSenseBanner";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, authClient, formatApiError } from "@/lib/auth";
import { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import SectionHeader from "@/components/SectionHeader";
import MinecraftSkin from "@/components/MinecraftSkin";
import { sfx } from "@/lib/sounds";
import {
  CheckCircle2,
  XCircle,
  Copy,
  Check,
  Loader2,
  MessageCircle,
  Boxes,
  Cake,
  Image as ImageIcon,
  ShieldCheck,
  ArrowRight,
  Trash2,
  Sparkles,
} from "lucide-react";

const POLL_MS = 4000;

/* --------------- Clipboard Helper --------------- */
async function copyToClipboard(text) {
  if (!text) {
    throw new Error("Nothing to copy");
  }

  // Attempt the modern Clipboard API first.
  try {
    if (
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function"
    ) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (error) {
    console.warn("Clipboard API unavailable:", error.message);
  }

  // Fallback: select the text so the user can copy it manually.
  const input = document.createElement("textarea");
  input.value = text;
  input.readOnly = true;

  Object.assign(input.style, {
    position: "fixed",
    left: "0",
    top: "0",
    width: "100%",
    height: "48px",
    padding: "10px",
    background: "#111",
    color: "#fff",
    border: "2px solid #FFBF00",
    zIndex: "2147483647",
    fontSize: "16px",
    opacity: "1",
  });

  document.body.appendChild(input);

  try {
    input.focus();
    input.select();
    input.setSelectionRange(0, input.value.length);

    const copied = document.execCommand("copy");

    if (copied) {
      return true;
    }

    throw new Error("Automatic copying is blocked");
  } finally {
    // Keep the selected command visible if automatic copying failed.
    // The caller can let the user copy it manually.
    if (input.isConnected) {
      input.remove();
    }
  }
}

/* --------------- Countdown Hook --------------- */
function useCountdown(seconds) {
  const [left, setLeft] = useState(seconds || 0);

  useEffect(() => {
    setLeft(seconds || 0);
  }, [seconds]);

  useEffect(() => {
    if (!left) return;

    const id = setInterval(() => {
      setLeft((s) => Math.max(0, s - 1));
    }, 1000);

    return () => clearInterval(id);
  }, [left ? 1 : 0]); // eslint-disable-line

  const mm = String(Math.floor(left / 60)).padStart(2, "0");
  const ss = String(left % 60).padStart(2, "0");

  return { left, label: `${mm}:${ss}` };
}

/* --------------- Verification Row --------------- */
function Row({ ok, label }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-b-0">
      <span className="text-sm text-white/80">{label}</span>

      {ok ? (
        <span className="inline-flex items-center gap-1 text-green-400 text-xs uppercase tracking-widest">
          <CheckCircle2 size={13} /> Verified
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-red-400 text-xs uppercase tracking-widest">
          <XCircle size={13} /> Not linked
        </span>
      )}
    </div>
  );
}

/* --------------- Discord Card --------------- */
function DiscordCard({ status, onChange }) {
  const [state, setState] = useState({
    code: "",
    command: "",
    remaining: 0,
    loading: false,
    revealed: false,
    copied: false,
  });

  const { label, left } = useCountdown(state.remaining);

  const requestCode = async () => {
    if (state.loading) return;

    setState((s) => ({ ...s, loading: true }));

    try {
      const { data } = await authClient.post("/verify/discord/code");

      if (data.verified) {
        await onChange?.();
        return;
      }

      setState({
        code: data.code,
        command: data.command,
        remaining: data.remaining,
        loading: false,
        revealed: true,
        copied: false,
      });

      sfx.click();
    } catch (e) {
      toast.error(formatApiError(e));
      setState((s) => ({ ...s, loading: false }));
    }
  };

  const copy = async () => {
    try {
      await copyToClipboard(state.command);

      setState((s) => ({ ...s, copied: true }));
      sfx.copy();
      toast.success("Command copied!");

      setTimeout(() => {
        setState((s) => ({ ...s, copied: false }));
      }, 1500);
    } catch (error) {
      console.error("Copy error:", error);

      toast.error(
        "Automatic copy is blocked. Please select the command and press Ctrl+C."
      );
    }
  };

  const unlink = async () => {
    if (!window.confirm("Unlink Discord account?")) return;

    try {
      await authClient.delete("/verify/discord");

      sfx.no();
      toast.success("Discord unlinked");
      setState({
        code: "",
        command: "",
        remaining: 0,
        loading: false,
        revealed: false,
        copied: false,
      });

      await onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (status?.discord_verified) {
    return (
      <div
        className="rounded-2xl glass p-6"
        data-testid="verify-discord-linked"
      >
        <div className="flex items-center gap-2 mb-2">
          <MessageCircle size={16} className="text-[#5865F2]" />
          <h3 className="font-hero text-2xl">Discord Linked</h3>
          <CheckCircle2 size={16} className="text-green-400 ml-auto" />
        </div>

        <div className="text-sm text-white/70">
          <span className="text-white/40">Discord ID:</span>{" "}
          <span className="font-mono text-[#FFBF00]">
            {status.discord_id}
          </span>
        </div>

        <button
          onClick={unlink}
          data-testid="verify-discord-unlink"
          className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-full border border-red-400/40 hover:bg-red-400/10 text-red-400 text-[11px] uppercase tracking-widest"
        >
          <Trash2 size={12} /> Unlink Discord
        </button>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl glass p-6"
      data-testid="verify-discord-pending"
    >
      <div className="flex items-center gap-2 mb-2">
        <MessageCircle size={16} className="text-[#5865F2]" />
        <h3 className="font-hero text-2xl">Link Discord</h3>
      </div>

      <ol className="text-[13px] text-white/70 space-y-1 list-decimal list-inside mb-4">
        <li>Open Discord and DM the LostDC bot.</li>
        <li>Click the button below to generate your one-time code.</li>
        <li>Send the exact command shown to the bot within 2 minutes.</li>
      </ol>

      {!state.revealed || left === 0 ? (
        <button
          onClick={requestCode}
          disabled={state.loading}
          data-testid="verify-discord-getcode"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#5865F2] hover:bg-[#4752c4] text-white text-xs uppercase tracking-widest font-semibold disabled:opacity-60"
        >
          {state.loading ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <Sparkles size={13} />
          )}

          {left === 0 && state.revealed
            ? "Code expired — get a new one"
            : "Get Discord Code"}
        </button>
      ) : (
        <>
          <button
            onClick={copy}
            data-testid="verify-discord-code"
            title="Click to copy Discord verification command"
            className="inline-flex items-center gap-2 px-4 py-3 rounded-xl font-mono text-lg bg-black border border-[#5865F2]/40 text-green-400 hover:border-[#FFBF00]/60 max-w-full"
          >
            <span className="break-all text-left">{state.command}</span>
            {state.copied ? (
              <Check size={14} className="text-[#FFBF00] shrink-0" />
            ) : (
              <Copy size={14} className="shrink-0" />
            )}
          </button>

          <div className="mt-2 text-[11px] uppercase tracking-widest text-white/50">
            Expires in{" "}
            <span className="text-[#FFBF00] font-mono">{label}</span>
          </div>
        </>
      )}
    </div>
  );
}

/* --------------- Minecraft Card --------------- */
function MinecraftCard({ status, onChange }) {
  const [state, setState] = useState({
    code: "",
    command: "",
    remaining: 0,
    loading: false,
    revealed: false,
    copied: false,
  });

  const { label, left } = useCountdown(state.remaining);

  const requestCode = async () => {
    if (state.loading) return;

    setState((s) => ({ ...s, loading: true }));

    try {
      const { data } = await authClient.post("/verify/mc/code");

      if (data.verified) {
        await onChange?.();
        return;
      }

      setState({
        code: data.code,
        command: data.command,
        remaining: data.remaining,
        loading: false,
        revealed: true,
        copied: false,
      });

      sfx.click();
    } catch (e) {
      toast.error(formatApiError(e));
      setState((s) => ({ ...s, loading: false }));
    }
  };

  const copy = async () => {
    try {
      await copyToClipboard(state.command);

      setState((s) => ({ ...s, copied: true }));
      sfx.copy();
      toast.success("Command copied!");

      setTimeout(() => {
        setState((s) => ({ ...s, copied: false }));
      }, 1500);
    } catch (error) {
      console.error("Copy error:", error);

      toast.error(
        "Automatic copy is blocked. Please select the command and press Ctrl+C."
      );
    }
  };

  const unlink = async () => {
    if (!window.confirm("Unlink Minecraft account?")) return;

    try {
      await authClient.delete("/verify/mc");

      sfx.no();
      toast.success("Minecraft unlinked");

      setState({
        code: "",
        command: "",
        remaining: 0,
        loading: false,
        revealed: false,
        copied: false,
      });

      await onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (status?.minecraft_verified) {
    return (
      <div
        className="rounded-2xl glass p-6"
        data-testid="verify-mc-linked"
      >
        <div className="flex items-center gap-2 mb-2">
          <Boxes size={16} className="text-[#FFBF00]" />
          <h3 className="font-hero text-2xl">Minecraft Linked</h3>
          <CheckCircle2 size={16} className="text-green-400 ml-auto" />
        </div>

        <div className="flex items-center gap-4 mt-2">
          <MinecraftSkin
            username={status.minecraft_username}
            mode="body"
            size={100}
          />

          <div>
            <div className="text-[11px] uppercase tracking-widest text-white/50">
              Username
            </div>
            <div className="font-hero text-2xl text-[#FFBF00]">
              {status.minecraft_username}
            </div>
          </div>
        </div>

        <button
          onClick={unlink}
          data-testid="verify-mc-unlink"
          className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-full border border-red-400/40 hover:bg-red-400/10 text-red-400 text-[11px] uppercase tracking-widest"
        >
          <Trash2 size={12} /> Unlink Minecraft
        </button>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl glass p-6"
      data-testid="verify-mc-pending"
    >
      <div className="flex items-center gap-2 mb-2">
        <Boxes size={16} className="text-[#FFBF00]" />
        <h3 className="font-hero text-2xl">Link Minecraft</h3>
      </div>

      <ol className="text-[13px] text-white/70 space-y-1 list-decimal list-inside mb-4">
        <li>
          Join{" "}
          <span className="font-mono text-[#FFBF00]">
            play.lostdrkc.in
          </span>{" "}
          in Minecraft.
        </li>
        <li>Click the button below to generate your one-time code.</li>
        <li>Run the exact command shown in chat within 5 minutes.</li>
      </ol>

      {!state.revealed || left === 0 ? (
        <button
          onClick={requestCode}
          disabled={state.loading}
          data-testid="verify-mc-getcode"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs uppercase tracking-widest font-semibold disabled:opacity-60"
        >
          {state.loading ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <Sparkles size={13} />
          )}

          {left === 0 && state.revealed
            ? "Code expired — get a new one"
            : "Get Minecraft Code"}
        </button>
      ) : (
        <>
          <button
            onClick={copy}
            data-testid="verify-mc-code"
            title="Click to copy Minecraft verification command"
            className="inline-flex items-center gap-2 px-4 py-3 rounded-xl font-mono text-lg bg-black border border-[#FFBF00]/40 text-green-400 hover:border-[#FF8C00]/60 max-w-full"
          >
            <span className="break-all text-left">{state.command}</span>
            {state.copied ? (
              <Check size={14} className="text-[#FFBF00] shrink-0" />
            ) : (
              <Copy size={14} className="shrink-0" />
            )}
          </button>

          <div className="mt-2 text-[11px] uppercase tracking-widest text-white/50">
            Expires in{" "}
            <span className="text-[#FFBF00] font-mono">{label}</span>
          </div>
        </>
      )}
    </div>
  );
}

/* --------------- DOB Card --------------- */
function DOBCard({ status, onChange }) {
  const [dob, setDob] = useState("");

  useEffect(() => {
    setDob(status?.dob || "");
  }, [status?.dob]);

  const save = async () => {
    if (!dob) {
      toast.error("Please select your date of birth");
      return;
    }

    try {
      await authClient.post("/verify/dob", { dob });
      sfx.pling();
      toast.success("Date of birth saved");
      await onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove date of birth?")) return;

    try {
      await authClient.delete("/verify/dob");
      setDob("");
      sfx.no();
      toast.success("Date of birth removed");
      await onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const done = !!status?.dob;

  return (
    <div className="rounded-2xl glass p-6" data-testid="verify-dob-card">
      <div className="flex items-center gap-2 mb-2">
        <Cake size={16} className="text-[#FF8C00]" />
        <h3 className="font-hero text-2xl">Date of Birth</h3>
        {done && (
          <CheckCircle2 size={16} className="text-green-400 ml-auto" />
        )}
      </div>

      <p className="text-sm text-white/60 mb-3">
        Required for future rewards &amp; personalization.
      </p>

      <div className="flex gap-2">
        <input
          type="date"
          value={dob}
          onChange={(e) => setDob(e.target.value)}
          data-testid="verify-dob-input"
          className="flex-1 min-w-0 bg-black/60 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#FFBF00]/40"
        />

        <button
          onClick={save}
          data-testid="verify-dob-save"
          className="px-4 py-2 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-[11px] uppercase tracking-widest font-semibold"
        >
          Save
        </button>
      </div>

      {done && (
        <button
          onClick={remove}
          className="mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-red-400 hover:text-red-300"
        >
          <Trash2 size={11} /> Remove DOB
        </button>
      )}
    </div>
  );
}

/* --------------- Skin Card --------------- */
function SkinCard({ status, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const done = !!status?.skin;

  const onFile = async (file) => {
    if (!file) return;

    if (
      file.type !== "image/png" ||
      !file.name.toLowerCase().endsWith(".png")
    ) {
      toast.error("Please select a valid PNG image");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Skin image must be smaller than 2 MB");
      return;
    }

    setUploading(true);

    try {
      const image = await new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Failed to read image"));

        reader.readAsDataURL(file);
      });

      const dimensions = await new Promise((resolve, reject) => {
        const img = new window.Image();

        img.onload = () =>
          resolve({
            width: img.naturalWidth,
            height: img.naturalHeight,
          });

        img.onerror = () => reject(new Error("Invalid PNG image"));
        img.src = image;
      });

      if (
        dimensions.width !== 64 ||
        dimensions.height !== 64
      ) {
        toast.error("Minecraft skin must be exactly 64 × 64 pixels");
        return;
      }

      const response = await authClient.post("/verify/skin", { image });

      if (response.status < 200 || response.status >= 300) {
        throw new Error("Server rejected the skin upload");
      }

      sfx.pling();
      toast.success("Skin uploaded successfully!");

      await onChange?.();
    } catch (error) {
      console.error("Skin upload failed:", {
        message: error?.message,
        status: error?.response?.status,
        response: error?.response?.data,
        url: error?.config?.url,
      });

      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.message;

      toast.error(
        typeof detail === "string"
          ? detail
          : error?.message || "Skin upload failed. Please try again."
      );
    } finally {
      setUploading(false);

      if (fileRef.current) {
        fileRef.current.value = "";
      }
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove uploaded skin?")) return;

    try {
      await authClient.delete("/verify/skin");
      sfx.no();
      toast.success("Skin removed");
      await onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="rounded-2xl glass p-6" data-testid="verify-skin-card">
      <div className="flex items-center gap-2 mb-2">
        <ImageIcon size={16} className="text-[#FFBF00]" />
        <h3 className="font-hero text-2xl">Upload Skin</h3>

        {done && (
          <CheckCircle2 size={16} className="text-green-400 ml-auto" />
        )}
      </div>

      <p className="text-sm text-white/60 mb-3">
        Upload your Minecraft skin PNG (64×64).
      </p>

      <div className="flex items-center gap-3">
        {done ? (
          <img
            src={fileUrl(status.skin)}
            alt="Minecraft skin"
            className="w-16 h-16 rounded-md border border-white/10 object-cover"
            style={{ imageRendering: "pixelated" }}
          />
        ) : (
          <div className="w-16 h-16 rounded-md border border-dashed border-white/10 grid place-items-center text-white/30">
            <ImageIcon size={20} />
          </div>
        )}

        <input
          ref={fileRef}
          type="file"
          accept="image/png,.png"
          hidden
          onChange={(e) => onFile(e.target.files?.[0])}
          data-testid="verify-skin-file"
        />

        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="verify-skin-upload"
          className="px-4 py-2 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-[11px] uppercase tracking-widest font-semibold disabled:opacity-60 inline-flex items-center gap-2"
        >
          {uploading && <Loader2 size={13} className="animate-spin" />}
          {uploading
            ? "Uploading..."
            : done
              ? "Change Skin"
              : "Upload Skin"}
        </button>
      </div>

      {done && status.skin && (
        <button
          type="button"
          onClick={async () => {
            try {
              const link = new URL(
                fileUrl(status.skin),
                window.location.origin
              ).href;
              await copyToClipboard(link);
              toast.success("Skin link copied!");
            } catch (error) {
              console.error("Copy skin link failed:", error);
              toast.error("Could not copy skin link");
            }
          }}
          className="mt-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-[10px] uppercase tracking-widest text-white/80 hover:bg-white/10 transition-colors"
        >
          <Copy size={12} />
          Copy Skin Link
        </button>
      )}

      {done && (
        <button
          onClick={remove}
          className="mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-red-400 hover:text-red-300"
        >
          <Trash2 size={11} /> Remove Skin
        </button>
      )}
    </div>
  );
}

/* --------------- Main Verify Page --------------- */
export default function Verify() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);

  const load = async () => {
    try {
      const { data } = await authClient.get("/verify/status");
      setStatus(data);
    } catch (err) {
      console.warn("Verify status failed:", err?.message);
    }
  };

  useEffect(() => {
    if (!loading && !user) {
      nav("/");
    }
  }, [loading, user, nav]);

  useEffect(() => {
    if (user) {
      load();
    }
  }, [user]); // eslint-disable-line

  // Poll until all verification fields are complete
  useEffect(() => {
    if (!user) return;

    if (
      status?.discord_verified &&
      status?.minecraft_verified &&
      status?.dob &&
      status?.skin
    ) {
      return;
    }

    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [
    user,
    status?.discord_verified,
    status?.minecraft_verified,
    status?.dob,
    status?.skin,
  ]); // eslint-disable-line

  if (loading || !user || !status) {
    return (
      <div
        className="pt-40 text-center text-white/50"
        data-testid="verify-loading"
      >
        Loading verification…
      </div>
    );
  }

  const percent =
    (status.gmail_verified ? 25 : 0) +
    (status.discord_verified ? 25 : 0) +
    (status.minecraft_verified ? 25 : 0) +
    (status.dob ? 12.5 : 0) +
    (status.skin ? 12.5 : 0);

  return (
    <div className="pt-32 pb-24 px-4 sm:px-6" data-testid="page-verify">
      <div className="max-w-5xl mx-auto">
        <SectionHeader
          eyebrow="Get Started"
          title="Verify Your LostDC ID"
          subtitle="Link your Gmail, Discord and Minecraft accounts to unlock the full network."
        />

        {/* Verification Status */}
        <div
          className="rounded-2xl glass p-6 mb-6"
          data-testid="verify-status"
        >
          <div className="flex items-center gap-3 mb-3">
            <ShieldCheck size={16} className="text-[#FFBF00]" />

            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">
              Verification Status
            </div>

            <div className="ml-auto text-xs font-mono text-white/50">
              {Math.round(percent)}% complete
            </div>
          </div>

          <div className="h-2 rounded-full bg-white/5 overflow-hidden mb-4">
            <div
              className="h-full transition-all duration-500"
              style={{
                width: `${percent}%`,
                background: "linear-gradient(90deg,#FFBF00,#FF8C00)",
              }}
            />
          </div>

          <Row
            ok={status.gmail_verified}
            label={`Gmail — ${status.gmail || "not set"}`}
          />

          <Row
            ok={status.discord_verified}
            label={`Discord${status.discord_id ? ` — ${status.discord_id}` : ""}`}
          />

          <Row
            ok={status.minecraft_verified}
            label={`Minecraft${status.minecraft_username ? ` — ${status.minecraft_username}` : ""}`}
          />

          <Row ok={!!status.dob} label="Date of Birth" />
          <Row ok={!!status.skin} label="Minecraft Skin" />

          {status.discord_verified && status.minecraft_verified && (
            <div className="mt-3 text-sm text-green-400 font-bold flex items-center gap-1">
              <Sparkles size={13} /> All core verifications complete!
            </div>
          )}

          {status.discord_channel_id && (
            <div className="mt-1 text-xs text-white/60">
              Your private channel:{" "}
              <span className="font-mono text-[#FFBF00]">
                #{status.discord_channel_id}
              </span>
            </div>
          )}
        </div>

        {/* Verification Cards */}
        <div className="grid md:grid-cols-2 gap-4">
          <DiscordCard status={status} onChange={load} />
          <MinecraftCard status={status} onChange={load} />
          <DOBCard status={status} onChange={load} />
          <SkinCard status={status} onChange={load} />
        </div>

        {status.discord_verified && status.minecraft_verified && (
          <div className="mt-8 text-center">
            <a
              href="/profile"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-sm uppercase tracking-widest font-semibold"
            >
              Continue to My Profile <ArrowRight size={14} />
            </a>
          </div>
        )}

        {/* Bottom AdSense Banner */}
        <div className="mt-12">
          <AdSenseBanner />
        </div>
      </div>
    </div>
  );
}